import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { createBaseResponseSchema } from '@server/presentation/schema'
import { z } from 'zod'

export const jpTrialEmailVerificationStatusSchema = z.enum([
  'READY',
  'CODE_SENT',
  'COOLDOWN',
  'VERIFIED',
  'INVALID_CODE',
  'ATTEMPTS_EXCEEDED',
  'CODE_EXPIRED',
  'LOCKED',
  'DUPLICATE_EMAIL',
  'RATE_LIMITED',
  'SEND_FAILED',
  'INVALIDATED',
])

export type JpTrialEmailVerificationStatus = z.infer<typeof jpTrialEmailVerificationStatusSchema>

const emailVerificationDataSchema = z
  .object({
    status: jpTrialEmailVerificationStatusSchema,
    email: z.string().nullable().optional(),
    verified: z.boolean(),
    verification_required: z.boolean().optional(),
    verificationRequired: z.boolean().optional(),
    server_time: z.number().nullable().optional(),
    serverTime: z.number().nullable().optional(),
    expires_at: z.number().nullable().optional(),
    expiresAt: z.number().nullable().optional(),
    resend_available_at: z.number().nullable().optional(),
    resendAvailableAt: z.number().nullable().optional(),
    retry_after_seconds: z.number().nullable().optional(),
    retryAfterSeconds: z.number().nullable().optional(),
    remaining_attempts: z.number().nullable().optional(),
    remainingAttempts: z.number().nullable().optional(),
  })
  .transform((data) => ({
    status: data.status,
    email: data.email ?? null,
    verified: data.verified,
    verificationRequired: data.verification_required ?? data.verificationRequired ?? !data.verified,
    serverTime: data.server_time ?? data.serverTime ?? null,
    expiresAt: data.expires_at ?? data.expiresAt ?? null,
    resendAvailableAt: data.resend_available_at ?? data.resendAvailableAt ?? null,
    retryAfterSeconds: data.retry_after_seconds ?? data.retryAfterSeconds ?? null,
    remainingAttempts: data.remaining_attempts ?? data.remainingAttempts ?? null,
  }))

const emailVerificationResponseSchema = createBaseResponseSchema(emailVerificationDataSchema)

export type JpTrialEmailVerification = z.infer<typeof emailVerificationDataSchema>

const request = async (
  method: 'GET' | 'POST',
  url: string,
  bearerToken: string,
  body?: Record<string, string>,
): Promise<JpTrialEmailVerification> => {
  const response = await podoFetcher<unknown>({
    method,
    url,
    ...(body ? { body } : {}),
    header: {
      Authorization: `Bearer ${bearerToken}`,
    },
  })

  return emailVerificationResponseSchema.parse(response).data
}

export const requestJpTrialEmailVerificationStatus = (bearerToken: string) =>
  request('GET', '/api/v1/onboarding/email-verification/status', bearerToken)

export const requestJpTrialEmailVerificationCode = (email: string, bearerToken: string) =>
  request('POST', '/api/v1/onboarding/email-verification/requests', bearerToken, { email })

export const confirmJpTrialEmailVerificationCode = (email: string, code: string, bearerToken: string) =>
  request('POST', '/api/v1/onboarding/email-verification/confirmations', bearerToken, { email, code })

export const invalidateJpTrialEmailVerificationCode = (email: string, bearerToken: string) =>
  request('POST', '/api/v1/onboarding/email-verification/invalidations', bearerToken, { email })
